/**
 * custom-filter.js
 * Client-side filtering for Shopify collection pages (POC).
 *
 * RUNTIME RULES:
 *   1. Filter verbergen als <2 distinct values beschikbaar zijn op de pagina
 *   2. Range-filter met 2 distinct values → fallback naar checkboxes
 *   3. Logic binnen 1 filter: OR (meerdere aangevinkt = meer resultaten)
 *   4. Logic tussen filters: AND (meer filters actief = minder resultaten)
 *
 * DEBUG:
 *   Zet DEBUG = true voor console-logs en window.cfState (introspectie via DevTools).
 *
 * ACTIVATION:
 *   Script doet early-exit als er geen <script id="cf-config"> op de pagina is.
 *   Die tag wordt alleen door custom-filter-config.liquid geëmit op configured
 *   collections (amerikaanse-koelkasten).
 */
(function () {
  "use strict";

  // =========================================================================
  // 1. CONSTANTS + DEBUG
  // =========================================================================
  const DEBUG = true;

  // Min distinct distinct-values for a filter to be shown.
  const MIN_DISTINCT = 2;

  // Range-filter fallback threshold. If a range-filter has exactly this many
  // distinct values, render as checkboxes instead of slider (poor UX with
  // only 2 stops on a slider).
  const RANGE_FALLBACK_THRESHOLD = 2;

  // Debounce for range-slider input events (ms).
  const RANGE_DEBOUNCE_MS = 150;

  // Client-side pagination: max producten per pagina (Pro-Witgoed wens).
  const PAGE_SIZE = 12;

  // Selector for product cards on the page (emitted by custom-filter-data.liquid).
  const CARD_SELECTOR = "[data-cf-filters]";

  // Unit + step mapping by metafield key-suffix. Covers the amerikaanse-koelkasten
  // POC filters; extend per POC as needed.
  const UNIT_MAP = {
    _cm: { unit: "cm", step: 1 },
    _kg: { unit: "kg", step: 0.1 },
    _kg_24u: { unit: "kg/24u", step: 0.1 },
    _liter: { unit: "L", step: 1 },
    _db: { unit: "dB", step: 1 },
    _kwh: { unit: "kWh", step: 1 },
    _w: { unit: "W", step: 10 },
    _tpm: { unit: "tpm", step: 50 },
    _uur: { unit: "u", step: 1 },
    _jaren: { unit: "jaar", step: 1 },
    _m__u: { unit: "m³/u", step: 10 },
  };

  // =========================================================================
  // 2. UTILITIES
  // =========================================================================
  const log = (...args) => DEBUG && console.log("[CF]", ...args);
  const warn = (...args) => console.warn("[CF]", ...args);

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
    );
  }

  function debounce(fn, ms) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  function unitForKey(shortKey) {
    // Match longest suffix first (e.g. _kg_24u before _kg).
    const sorted = Object.keys(UNIT_MAP).sort((a, b) => b.length - a.length);
    for (const suffix of sorted) {
      if (shortKey.endsWith(suffix)) return UNIT_MAP[suffix];
    }
    return { unit: "", step: 1 };
  }

  // Collator for stable, locale-aware enum sort.
  const collator = new Intl.Collator("nl", { numeric: true });

  // =========================================================================
  // 3. INITIALIZATION (entry point)
  // =========================================================================
  function init() {
    const configEl = document.getElementById("cf-config");
    if (!configEl) {
      log("No #cf-config script tag — not a configured collection. Exit.");
      return;
    }

    const configJson = (() => {
      try { return JSON.parse(configEl.textContent); }
      catch (e) { warn("Failed to parse config JSON:", e); return null; }
    })();
    if (!configJson) return;

    const collectionHandle = configEl.dataset.collection;
    const collectionConfig = configJson[collectionHandle];
    if (!collectionConfig) {
      warn(`No config entry for collection "${collectionHandle}"`);
      return;
    }

    const drawer = document.getElementById("cf-drawer");
    const filterList = drawer && drawer.querySelector(".cf-filter-list");
    if (!filterList) {
      warn("Drawer / .cf-filter-list not found — is custom-filter-ui rendered?");
      return;
    }

    const cards = collectCards();
    if (cards.length === 0) {
      log("No cards with [data-cf-filters] found on page — nothing to filter.");
      return;
    }

    log(`Initialized for collection "${collectionHandle}" with ${collectionConfig.filters.length} configured filters and ${cards.length} products`);

    // Derive runtime-ready filter objects (with visibility, distinct values, etc.)
    const filters = resolveFilters(collectionConfig.filters, cards);

    const visible = filters.filter(f => f._visible);
    const hidden = filters.filter(f => !f._visible);
    log(`Filters visible: ${visible.length}, hidden: ${hidden.length}`);
    if (hidden.length > 0) {
      log("Hidden filters (insufficient data):", hidden.map(f => ({ key: f.key, reason: f._hideReason })));
    }

    // Set up shared state.
    const state = {
      collection: collectionHandle,
      cards: cards,
      filters: visible,                     // only visible filters drive UI
      activeByKey: new Map(),               // shortKey -> Set<string> | {min,max}
      visibleCount: cards.length,
      currentPage: 1,                       // client-side pagination
      matchedCards: cards.slice(),          // cards matching filters (pre-pagination)
    };

    if (DEBUG) window.cfState = state;

    // Initial URL state → populate activeByKey.
    readUrlIntoState(state);

    // Render UI + attach handlers.
    renderFilterList(state, filterList);
    renderActiveChips(state);
    attachDrawerHandlers();
    moveMobileToggleToToolbar();
    attachClearAll(state);
    attachPaginationDelegation(state);

    // Initial apply + count.
    applyFilters(state);

    // popstate (back/forward).
    window.addEventListener("popstate", () => {
      readUrlIntoState(state);
      syncUiFromState(state);
      applyFilters(state);
      renderActiveChips(state);
    });
  }

  // =========================================================================
  // 4. CARD DATA EXTRACTION
  // =========================================================================
  function collectCards() {
    const els = Array.from(document.querySelectorAll(CARD_SELECTOR));
    return els.map(el => {
      let values = {};
      try { values = JSON.parse(el.dataset.cfFilters || "{}"); }
      catch { values = {}; }
      // Voeg prijs toe als virtuele key (uit data-cf-price, in EUR)
      const priceAttr = el.dataset.cfPrice;
      if (priceAttr !== undefined) {
        const p = parseFloat(priceAttr);
        if (!isNaN(p)) values.price = p;
      }
      // Voeg product_type toe als virtuele key (uit data-cf-product-type)
      const typeAttr = el.dataset.cfProductType;
      if (typeAttr) values.product_type = typeAttr;
      return { el, id: el.dataset.cfId, title: el.dataset.cfTitle, values };
    });
  }

  // =========================================================================
  // 5. RUNTIME RULES — visibility + fallback
  // =========================================================================
  function resolveFilters(configFilters, cards) {
    return configFilters.map(f => {
      const shortKey = f.key.replace(/^(custom|shopify)\./, "");

      // Compound filter: virtuele filter zonder direct metafield (options met rules)
      if (f.ui_type === "compound") {
        return { ...f, shortKey, _visible: true, _uiType: "compound" };
      }

      const distinct = new Set();
      for (const card of cards) {
        const v = card.values[shortKey];
        if (v === undefined || v === null || v === "") continue;
        if (Array.isArray(v)) v.forEach(x => distinct.add(x));
        else distinct.add(v);
      }

      // Rule 1: <2 distinct → hide.
      if (distinct.size < MIN_DISTINCT) {
        return { ...f, shortKey, _visible: false, _hideReason: `only ${distinct.size} distinct value(s)` };
      }

      // Rule 2: range with exactly 2 distinct → fallback to checkboxes.
      let uiType = f.ui_type;
      if (uiType === "range" && distinct.size === RANGE_FALLBACK_THRESHOLD) {
        uiType = "checkbox";
        log(`Fallback for ${shortKey}: range → checkbox (only ${distinct.size} distinct values)`);
      }

      const resolved = {
        ...f, shortKey, _visible: true, _uiType: uiType, _distinct: distinct,
        ...unitForKey(shortKey),
      };
      if (uiType === "range") {
        const nums = [...distinct].map(Number).filter(n => !Number.isNaN(n)).sort((a, b) => a - b);
        resolved._min = nums[0];
        resolved._max = nums[nums.length - 1];
      } else {
        // Sort enum/checkbox options.
        resolved._options = [...distinct].sort((a, b) =>
          f.type === "number_integer" || f.type === "number_decimal"
            ? Number(a) - Number(b)
            : collator.compare(String(a), String(b))
        );
      }
      return resolved;
    });
  }

  // =========================================================================
  // 6. URL STATE SYNC (parse + serialize)
  // =========================================================================
  function readUrlIntoState(state) {
    const params = new URLSearchParams(location.search);
    const next = new Map();
    for (const f of state.filters) {
      const raw = params.get(f.shortKey);
      if (!raw) continue;
      if (f._uiType === "range") {
        const m = raw.match(/^(-?[\d.]+)-(-?[\d.]+)$/);
        if (!m) continue;
        const lo = Number(m[1]), hi = Number(m[2]);
        if (!Number.isNaN(lo) && !Number.isNaN(hi) && lo <= hi) {
          next.set(f.shortKey, { min: lo, max: hi });
        }
      } else if (f._uiType === "bucket") {
        // bucket: comma-separated indices
        const vals = raw.split(",").filter(Boolean);
        const valid = vals.filter(v => {
          const i = parseInt(v, 10);
          return !isNaN(i) && i >= 0 && i < (f.buckets || []).length;
        });
        if (valid.length) next.set(f.shortKey, new Set(valid));
      } else {
        const vals = raw.split(",").filter(Boolean);
        // Validate against known options (ignore invalid values).
        const valid = vals.filter(v => f._options.some(o => String(o) === v));
        if (valid.length) next.set(f.shortKey, new Set(valid));
      }
    }
    state.activeByKey = next;
    // ?page= behoud: respect pagina-state bij refresh/back-navigation
    const pageParam = parseInt(params.get("page"), 10);
    if (!isNaN(pageParam) && pageParam > 0) {
      state.currentPage = pageParam;
    }
  }

  function writeUrlFromState(state, push = true) {
    const params = new URLSearchParams(location.search);
    // Remove all filter keys first.
    for (const f of state.filters) params.delete(f.shortKey);
    // Filter-change reset paginatie, dus ?page= weg
    params.delete("page");
    // Re-add active ones.
    for (const [shortKey, val] of state.activeByKey) {
      const f = state.filters.find(x => x.shortKey === shortKey);
      if (!f) continue;
      if (f._uiType === "range") {
        if (val.min !== f._min || val.max !== f._max) {
          params.set(shortKey, `${val.min}-${val.max}`);
        }
      } else if (val instanceof Set && val.size > 0) {
        params.set(shortKey, [...val].join(","));
      }
    }
    const qs = params.toString();
    const url = location.pathname + (qs ? "?" + qs : "") + location.hash;
    if (push) history.pushState(null, "", url);
    else history.replaceState(null, "", url);
  }

  // =========================================================================
  // 7. FILTER APPLICATION (OR within, AND between)
  // =========================================================================
  // Facet-count helper: alleen cards die alle andere actieve filters matchen tellen mee
  // voor de count van filter X. Bol/Coolblue-stijl: als je "Amerikaanse koelkasten"
  // selecteert moeten de Kleur-counts alleen amerikaanse koelkasten tellen.
  function cardMatchesExcluding(card, state, excludeShortKey) {
    for (const [shortKey, active] of state.activeByKey) {
      if (shortKey === excludeShortKey) continue;
      const filter = state.filters.find(f => f.shortKey === shortKey);
      if (!filter) continue;
      if (!matchesOneFilter(card, filter, active)) return false;
    }
    return true;
  }

  function matchesOneFilter(card, filter, active) {
    if (filter._uiType === "compound") {
      if (active.size === 0) return true;
      for (const optId of active) {
        const opt = filter.options.find(o => o.id === optId);
        if (opt && compoundOptionMatches(card, opt)) return true;
      }
      return false;
    }
    const cardVal = card.values[filter.shortKey];
    if (filter._uiType === "range") {
      if (cardVal === undefined || cardVal === null || cardVal === "") return false;
      const n = Number(cardVal);
      return !(Number.isNaN(n) || n < active.min || n > active.max);
    }
    if (filter._uiType === "bucket") {
      if (active.size === 0) return true;
      if (cardVal === undefined || cardVal === null || cardVal === "") return false;
      const n = Number(cardVal);
      if (Number.isNaN(n)) return false;
      for (const idxStr of active) {
        const bucket = filter.buckets[parseInt(idxStr, 10)];
        if (!bucket) continue;
        const okMin = bucket.min === undefined || n >= bucket.min;
        const okMax = bucket.max === undefined || n < bucket.max;
        if (okMin && okMax) return true;
      }
      return false;
    }
    // checkbox
    if (active.size === 0) return true;
    if (cardVal === undefined || cardVal === null || cardVal === "") return false;
    const vals = Array.isArray(cardVal) ? cardVal : [cardVal];
    return vals.some(v => active.has(String(v)));
  }

  function cardMatches(card, state) {
    for (const [shortKey, active] of state.activeByKey) {
      const filter = state.filters.find(f => f.shortKey === shortKey);
      if (!filter) continue;

      // Compound: OR-logic — card matched als één van de geselecteerde options matched
      if (filter._uiType === "compound") {
        if (active.size === 0) continue;
        let anyMatch = false;
        for (const optId of active) {
          const opt = filter.options.find(o => o.id === optId);
          if (opt && compoundOptionMatches(card, opt)) { anyMatch = true; break; }
        }
        if (!anyMatch) return false;
        continue;
      }

      const cardVal = card.values[shortKey];

      if (filter._uiType === "range") {
        if (cardVal === undefined || cardVal === null || cardVal === "") return false;
        const n = Number(cardVal);
        if (Number.isNaN(n) || n < active.min || n > active.max) return false;
      } else if (filter._uiType === "bucket") {
        // bucket: active is a Set of bucket-indices (as strings). Card matches als value
        // binnen één van de geselecteerde bucket-grenzen valt (OR-logic binnen filter).
        if (active.size === 0) continue;
        if (cardVal === undefined || cardVal === null || cardVal === "") return false;
        const n = Number(cardVal);
        if (Number.isNaN(n)) return false;
        let anyMatch = false;
        for (const idxStr of active) {
          const bucket = filter.buckets[parseInt(idxStr, 10)];
          if (!bucket) continue;
          const okMin = bucket.min === undefined || n >= bucket.min;
          const okMax = bucket.max === undefined || n < bucket.max;
          if (okMin && okMax) { anyMatch = true; break; }
        }
        if (!anyMatch) return false;
      } else {
        // checkbox (includes boolean + enum + range-fallback)
        if (active.size === 0) continue;
        if (cardVal === undefined || cardVal === null || cardVal === "") return false;
        const vals = Array.isArray(cardVal) ? cardVal : [cardVal];
        const matched = vals.some(v => active.has(String(v)));
        if (!matched) return false;
      }
    }
    return true;
  }

  function applyFilters(state) {
    let visible = 0;
    // Batch DOM updates in one frame.
    requestAnimationFrame(() => {
      // Eerst: bepaal welke cards matchen (filter-hide)
      const matched = [];
      for (const card of state.cards) {
        const show = cardMatches(card, state);
        card.el.toggleAttribute("data-cf-hidden", !show);
        if (show) {
          matched.push(card);
          visible++;
        }
      }
      state.visibleCount = visible;
      state.matchedCards = matched;
      // Paginatie toepassen op gematchte cards
      applyPagination(state);
      updateResultCount(state);
      updateEmptyState(state);
      renderPagination(state);
    });
  }

  function applyPagination(state) {
    const { matchedCards, currentPage } = state;
    const startIdx = (currentPage - 1) * PAGE_SIZE;
    const endIdx = startIdx + PAGE_SIZE;
    // Drie-laags hiding: inline style met !important + attribute + class.
    // Eén ervan zal het altijd doen, ongeacht CSS-overrides elders.
    for (const card of state.cards) {
      card.el.style.removeProperty("display");
      card.el.removeAttribute("data-cf-paged-out");
      card.el.classList.remove("cf-page-out");
    }
    matchedCards.forEach((card, i) => {
      const onPage = i >= startIdx && i < endIdx;
      if (!onPage) {
        card.el.style.setProperty("display", "none", "important");
        card.el.setAttribute("data-cf-paged-out", "");
        card.el.classList.add("cf-page-out");
      }
    });
  }

  function changePage(state, newPage) {
    // Defensief: zorg dat matchedCards up-to-date is voor we paginate.
    const fresh = [];
    for (const card of state.cards) {
      if (cardMatches(card, state)) fresh.push(card);
    }
    state.matchedCards = fresh;
    state.visibleCount = fresh.length;
    const totalPages = Math.max(1, Math.ceil(state.matchedCards.length / PAGE_SIZE));
    state.currentPage = Math.max(1, Math.min(newPage, totalPages));
    applyPagination(state);
    renderPagination(state);
    // Sync ?page= naar URL zodat refresh + back-navigation werkt
    try {
      const u = new URL(window.location.href);
      if (state.currentPage > 1) u.searchParams.set("page", String(state.currentPage));
      else u.searchParams.delete("page");
      history.replaceState(null, "", u.toString());
    } catch (e) { /* no-op */ }
    // Smooth scroll naar de top van het product-grid
    const grid = document.querySelector(".main-products-grid__results, [id*='product-grid'], .grid--product");
    if (grid) {
      const top = grid.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }

  function renderPagination(state) {
    let nav = document.querySelector(".cf-pagination");
    const totalPages = Math.max(1, Math.ceil(state.matchedCards.length / PAGE_SIZE));
    if (totalPages <= 1) {
      if (nav) nav.remove();
      return;
    }
    const gridResults = document.querySelector(".main-products-grid__results, [id*='product-grid']").closest(".main-products-grid__results, [id*='product-grid'], .main-products-grid, .grid-wrapper") || document.querySelector(".main-products-grid__results");
    const container = gridResults || document.body;
    if (!nav) {
      nav = document.createElement("nav");
      nav.className = "cf-pagination";
      nav.setAttribute("aria-label", "Paginatie");
      container.appendChild(nav);
    }
    const cur = state.currentPage;
    const pages = [];
    // Prev
    pages.push(`<button type="button" class="cf-pagination-btn cf-pagination-prev" ${cur === 1 ? "disabled" : ""} data-cf-page="${cur - 1}" aria-label="Vorige pagina">‹</button>`);
    // Page numbers (max 7 zichtbaar met ellipsis)
    const pageNums = buildPageList(cur, totalPages);
    for (const p of pageNums) {
      if (p === "…") {
        pages.push(`<span class="cf-pagination-ellipsis">…</span>`);
      } else {
        pages.push(`<button type="button" class="cf-pagination-btn ${p === cur ? "cf-pagination-active" : ""}" data-cf-page="${p}" ${p === cur ? 'aria-current="page"' : ""}>${p}</button>`);
      }
    }
    // Next
    pages.push(`<button type="button" class="cf-pagination-btn cf-pagination-next" ${cur === totalPages ? "disabled" : ""} data-cf-page="${cur + 1}" aria-label="Volgende pagina">›</button>`);
    nav.innerHTML = pages.join("");
    // Bind click events (single delegated)
  }

  // Global click delegation voor cf-pagination — robust tegen DOM-changes.
  function attachPaginationDelegation(state) {
    document.addEventListener("click", (e) => {
      const btn = e.target.closest(".cf-pagination-btn");
      if (!btn || btn.disabled) return;
      if (!btn.closest(".cf-pagination")) return;
      e.preventDefault();
      e.stopPropagation();
      const newPage = parseInt(btn.dataset.cfPage, 10);
      if (!isNaN(newPage)) changePage(state, newPage);
    });
  }

  function buildPageList(cur, total) {
    // Build a compact list like [1, '…', 4, 5, 6, '…', 10]
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const result = [1];
    if (cur > 4) result.push("…");
    for (let p = Math.max(2, cur - 2); p <= Math.min(total - 1, cur + 2); p++) result.push(p);
    if (cur < total - 3) result.push("…");
    result.push(total);
    return result;
  }

  function updateResultCount(state) {
    const el = document.querySelector(".cf-result-count");
    const total = state.cards.length;
    const shown = state.visibleCount;
    if (el) el.textContent = `Toont ${shown} van ${total} product${total === 1 ? "" : "en"}`;
    // Sync sticky mobile "Toon X resultaten" knop met live count
    const applyBtn = document.querySelector(".cf-apply");
    if (applyBtn) {
      applyBtn.textContent = `Toon ${shown} resultaat${shown === 1 ? "" : "en"}`;
    }
  }

  function updateEmptyState(state) {
    let el = document.querySelector(".cf-empty-message");
    if (state.visibleCount === 0) {
      if (!el) {
        const grid = document.querySelector("[id*='product-grid'], .grid--product, .collection-grid, .product-grid") || document.body;
        el = document.createElement("div");
        el.className = "cf-empty-message";
        el.innerHTML = '<p>Geen producten gevonden met deze filters.</p><button type="button" class="cf-empty-clear">Wis alle filters</button>';
        grid.appendChild(el);
        el.querySelector(".cf-empty-clear").addEventListener("click", () => clearAllFilters(state));
      }
      el.hidden = false;
    } else if (el) {
      el.hidden = true;
    }
  }

  // =========================================================================
  // 8. UI RENDERING
  // =========================================================================
  function renderFilterList(state, container) {
    const frag = document.createDocumentFragment();
    for (const f of state.filters) frag.appendChild(renderFilterBlock(state, f));
    container.innerHTML = "";
    container.appendChild(frag);
    syncUiFromState(state);
  }

  function renderFilterBlock(state, filter) {
    const block = document.createElement("details");
    block.className = "cf-filter";
    block.dataset.key = filter.shortKey;
    block.open = true;

    const summary = document.createElement("summary");
    summary.className = "cf-filter-summary";
    summary.innerHTML = `<span class="cf-filter-title">${escapeHtml(filter.display)}</span><span class="cf-chevron" aria-hidden="true"></span>`;
    block.appendChild(summary);

    const body = document.createElement("div");
    body.className = "cf-filter-body";
    if (filter._uiType === "range") body.appendChild(renderRangeFilter(state, filter));
    else if (filter._uiType === "bucket") body.appendChild(renderBucketFilter(state, filter));
    else if (filter._uiType === "compound") body.appendChild(renderCompoundFilter(state, filter));
    else body.appendChild(renderCheckboxFilter(state, filter));
    block.appendChild(body);

    return block;
  }

  function renderCompoundFilter(state, filter) {
    // Quick-pick filter: elke option heeft een rule (custom.key → value/array/min-max object)
    const wrap = document.createElement("div");
    wrap.className = "cf-checkbox-list";

    const activeSet = state.activeByKey.get(filter.shortKey);

    // Tel cards die matchen per option, gebaseerd op cards die OOK alle andere
    // actieve filters matchen (excl. deze compound).
    const pool = state.cards.filter(c => cardMatchesExcluding(c, state, filter.shortKey));
    const counts = filter.options.map(opt =>
      pool.filter(c => compoundOptionMatches(c, opt)).length
    );

    filter.options.forEach((opt, i) => {
      const isChecked = activeSet ? activeSet.has(opt.id) : false;
      const label = document.createElement("label");
      label.className = "cf-checkbox";
      if (counts[i] === 0 && !isChecked) label.classList.add("cf-option-zero");
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.value = opt.id;
      cb.checked = isChecked;
      cb.addEventListener("change", () => onCompoundChange(state, filter, opt.id, cb.checked));
      const text = document.createElement("span");
      text.className = "cf-checkbox-label";
      text.textContent = opt.label;
      const count = document.createElement("span");
      count.className = "cf-checkbox-count";
      count.textContent = `(${counts[i]})`;
      label.append(cb, text, count);
      wrap.appendChild(label);
    });

    // Compound-filter ("Wat vind je belangrijk?") is de hero-filter en moet
    // altijd alle opties tonen — geen collapse.
    return wrap;
  }

  function compoundOptionMatches(card, option) {
    // Rule format: { "custom.energieklasse": ["A","B"] } of { "custom.no_frost": true }
    // of { "custom.geluidsniveau_db": { "max": 40 } } etc.
    for (const [fullKey, rule] of Object.entries(option.rule)) {
      const shortKey = fullKey.replace(/^(custom|shopify)\./, "");
      const cardVal = card.values[shortKey];
      if (cardVal === undefined || cardVal === null || cardVal === "") return false;
      if (Array.isArray(rule)) {
        // Match if cardVal is any of the array values
        if (!rule.includes(String(cardVal))) return false;
      } else if (typeof rule === "object" && rule !== null) {
        // min/max range
        const n = Number(cardVal);
        if (Number.isNaN(n)) return false;
        if (rule.min !== undefined && n < rule.min) return false;
        if (rule.max !== undefined && n >= rule.max) return false;
      } else {
        // Direct value match (string/boolean/number)
        if (String(cardVal) !== String(rule)) return false;
      }
    }
    return true;
  }

  function onCompoundChange(state, filter, optId, checked) {
    let set = state.activeByKey.get(filter.shortKey);
    if (!set) {
      set = new Set();
      state.activeByKey.set(filter.shortKey, set);
    }
    if (checked) set.add(optId); else set.delete(optId);
    if (set.size === 0) state.activeByKey.delete(filter.shortKey);
    commit(state);
  }

  function renderBucketFilter(state, filter) {
    // Render buckets als checkbox-lijst. Per bucket label + count van cards die in die bucket vallen.
    const wrap = document.createElement("div");
    wrap.className = "cf-checkbox-list";

    // Tel cards per bucket, gebaseerd op cards die OOK alle andere actieve
    // filters matchen.
    const pool = state.cards.filter(c => cardMatchesExcluding(c, state, filter.shortKey));
    const counts = new Array(filter.buckets.length).fill(0);
    for (const card of pool) {
      const cardVal = Number(card.values[filter.shortKey]);
      if (Number.isNaN(cardVal)) continue;
      filter.buckets.forEach((b, i) => {
        const okMin = b.min === undefined || cardVal >= b.min;
        const okMax = b.max === undefined || cardVal < b.max;
        if (okMin && okMax) counts[i]++;
      });
    }

    const activeSet = state.activeByKey.get(filter.shortKey);

    filter.buckets.forEach((bucket, i) => {
      const isChecked = activeSet ? activeSet.has(String(i)) : false;
      const label = document.createElement("label");
      label.className = "cf-checkbox";
      if (counts[i] === 0 && !isChecked) label.classList.add("cf-option-zero");
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.value = String(i);
      cb.checked = activeSet ? activeSet.has(String(i)) : false;
      cb.addEventListener("change", () => onBucketChange(state, filter, String(i), cb.checked));
      const text = document.createElement("span");
      text.className = "cf-checkbox-label";
      text.textContent = bucket.label;
      const count = document.createElement("span");
      count.className = "cf-checkbox-count";
      count.textContent = `(${counts[i]})`;
      label.append(cb, text, count);
      wrap.appendChild(label);
    });

    applyCollapsibleOptions(wrap);
    return wrap;
  }

  function onBucketChange(state, filter, idxStr, checked) {
    let set = state.activeByKey.get(filter.shortKey);
    if (!set) {
      set = new Set();
      state.activeByKey.set(filter.shortKey, set);
    }
    if (checked) set.add(idxStr); else set.delete(idxStr);
    if (set.size === 0) state.activeByKey.delete(filter.shortKey);
    commit(state);
  }

  function renderCheckboxFilter(state, filter) {
    const wrap = document.createElement("div");
    wrap.className = "cf-checkbox-list";

    // Per-option card count, gebaseerd op cards die OOK alle andere actieve filters
    // matchen. Bol-stijl facet-counts.
    const pool = state.cards.filter(c => cardMatchesExcluding(c, state, filter.shortKey));
    const counts = new Map();
    for (const card of pool) {
      const v = card.values[filter.shortKey];
      if (v === undefined || v === null || v === "") continue;
      const vals = Array.isArray(v) ? v : [v];
      for (const x of vals) counts.set(String(x), (counts.get(String(x)) || 0) + 1);
    }

    // Sorteer opties op frequentie (meest voorkomend eerst), zodat populaire
    // waarden boven de "Meer"-knop staan. Uitzondering: energielabel heeft
    // een natuurlijke A→G volgorde, dus alfabetisch sorteren.
    const sortedOpts = [...filter._options].sort((a, b) => {
      if (filter.shortKey === "energieklasse") {
        return String(a).localeCompare(String(b));
      }
      const ca = counts.get(String(a)) || 0;
      const cb = counts.get(String(b)) || 0;
      if (cb !== ca) return cb - ca;
      return String(a).localeCompare(String(b));
    });

    for (const opt of sortedOpts) {
      const strVal = String(opt);
      const label = document.createElement("label");
      label.className = "cf-checkbox";
      if ((counts.get(strVal) || 0) === 0) label.classList.add("cf-option-zero");

      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.value = strVal;
      cb.dataset.filterKey = filter.shortKey;

      const text = document.createElement("span");
      text.className = "cf-checkbox-label";
      text.textContent = labelForOption(filter, opt);

      const count = document.createElement("span");
      count.className = "cf-checkbox-count";
      count.textContent = counts.get(strVal) || 0;

      label.append(cb, text, count);
      wrap.appendChild(label);

      cb.addEventListener("change", () => onCheckboxChange(state, filter, strVal, cb.checked));
    }
    applyCollapsibleOptions(wrap);
    return wrap;
  }

  // Threshold: aantal opties zichtbaar voordat "Meer" verschijnt.
  const MAX_VISIBLE_OPTIONS = 3;

  function applyCollapsibleOptions(wrap) {
    const opts = wrap.querySelectorAll(":scope > .cf-checkbox:not(.cf-option-zero)");
    if (opts.length <= MAX_VISIBLE_OPTIONS) return;

    // Verberg opties voorbij de drempel.
    opts.forEach((el, i) => {
      if (i >= MAX_VISIBLE_OPTIONS) el.classList.add("cf-option-hidden");
    });

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "cf-options-toggle";
    toggle.setAttribute("aria-expanded", "false");
    toggle.innerHTML = `<span class="cf-options-toggle-chevron" aria-hidden="true"></span><span class="cf-options-toggle-text">Meer</span>`;
    toggle.addEventListener("click", () => {
      const expanded = wrap.classList.toggle("cf-options-expanded");
      toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
      toggle.querySelector(".cf-options-toggle-text").textContent = expanded ? "Minder" : "Meer";
    });
    wrap.appendChild(toggle);
  }

  function labelForOption(filter, opt) {
    if (filter.type === "boolean") return opt === "true" || opt === true ? "Ja" : "Nee";
    // BOL-style prefix voor energielabel: "Energielabel A" i.p.v. "A".
    if (filter.shortKey === "energieklasse") return `Energielabel ${opt}`;
    const unit = filter.unit;
    return unit ? `${opt} ${unit}` : String(opt);
  }

  function renderRangeFilter(state, filter) {
    const frag = document.createDocumentFragment();
    const { _min: min, _max: max, step } = filter;

    const labels = document.createElement("div");
    labels.className = "cf-range-labels";
    const lblMin = document.createElement("span");
    const lblMax = document.createElement("span");
    lblMin.className = "cf-range-min-label";
    lblMax.className = "cf-range-max-label";
    labels.append(lblMin, lblMax);

    const slider = document.createElement("div");
    slider.className = "cf-range-slider";
    const fill = document.createElement("div");
    fill.className = "cf-range-fill";
    const inputMin = document.createElement("input");
    const inputMax = document.createElement("input");
    [inputMin, inputMax].forEach(i => {
      i.type = "range";
      i.min = String(min); i.max = String(max); i.step = String(step);
      i.className = "cf-range-input";
    });
    inputMin.classList.add("cf-range-input-min");
    inputMax.classList.add("cf-range-input-max");
    inputMin.value = String(min);
    inputMax.value = String(max);
    slider.append(fill, inputMin, inputMax);

    const updateVisual = () => {
      let lo = Number(inputMin.value), hi = Number(inputMax.value);
      if (lo > hi) [lo, hi] = [hi, lo];
      lblMin.textContent = formatRangeValue(lo, filter);
      lblMax.textContent = formatRangeValue(hi, filter);
      const span = max - min || 1;
      const leftPct = ((lo - min) / span) * 100;
      const rightPct = ((hi - min) / span) * 100;
      fill.style.left = `${leftPct}%`;
      fill.style.width = `${rightPct - leftPct}%`;
    };

    const debouncedCommit = debounce(() => {
      let lo = Number(inputMin.value), hi = Number(inputMax.value);
      if (lo > hi) [lo, hi] = [hi, lo];
      onRangeChange(state, filter, lo, hi);
    }, RANGE_DEBOUNCE_MS);

    // Alleen commit triggeren voor echte user input (e.isTrusted = true).
    // syncUiFromState dispatcht programmatische input-events; die mogen alleen
    // het visuele update doen — anders wordt commit getriggerd ná pagination-click
    // en reset commit currentPage terug naar 1.
    inputMin.addEventListener("input", (e) => { updateVisual(); if (e.isTrusted) debouncedCommit(); });
    inputMax.addEventListener("input", (e) => { updateVisual(); if (e.isTrusted) debouncedCommit(); });

    updateVisual();
    // Volgorde: labels + (text-inputs als position=top) + slider + (text-inputs als position=bottom)
    const hasInputs = !!filter.with_inputs;
    const inputsTop = hasInputs && (filter.inputs_position === "top" || filter.inputs_position === undefined);
    frag.appendChild(labels);

    let inputsRow = null;
    if (hasInputs) {
      inputsRow = document.createElement("div");
      inputsRow.className = "cf-range-text-inputs";
      const prefix = filter.unit_prefix || "";
      const suffix = filter.unit && !filter.unit_prefix ? ` ${filter.unit}` : "";
      const txtMin = document.createElement("input");
      const txtMax = document.createElement("input");
      [txtMin, txtMax].forEach(i => {
        i.type = "number";
        i.min = String(min);
        i.max = String(max);
        i.step = String(step);
        i.inputMode = "numeric";
        i.className = "cf-range-text-input";
      });
      txtMin.value = String(min);
      txtMax.value = String(max);
      txtMin.setAttribute("aria-label", `${filter.display} minimum`);
      txtMax.setAttribute("aria-label", `${filter.display} maximum`);
      const wrap1 = document.createElement("label");
      const wrap2 = document.createElement("label");
      wrap1.className = "cf-range-text-wrap";
      wrap2.className = "cf-range-text-wrap";
      if (prefix) {
        const p1 = document.createElement("span"); p1.className = "cf-range-text-prefix"; p1.textContent = prefix;
        const p2 = document.createElement("span"); p2.className = "cf-range-text-prefix"; p2.textContent = prefix;
        wrap1.appendChild(p1); wrap2.appendChild(p2);
      }
      wrap1.appendChild(txtMin); wrap2.appendChild(txtMax);
      const dash = document.createElement("span");
      dash.className = "cf-range-text-dash";
      dash.textContent = "tot";
      inputsRow.append(wrap1, dash, wrap2);

      // Sync slider → text + text → slider
      const syncText = () => {
        txtMin.value = String(Math.min(Number(inputMin.value), Number(inputMax.value)));
        txtMax.value = String(Math.max(Number(inputMin.value), Number(inputMax.value)));
      };
      inputMin.addEventListener("input", syncText);
      inputMax.addEventListener("input", syncText);

      const commitFromText = () => {
        let lo = Math.max(min, Math.min(max, Number(txtMin.value) || min));
        let hi = Math.max(min, Math.min(max, Number(txtMax.value) || max));
        if (lo > hi) [lo, hi] = [hi, lo];
        inputMin.value = String(lo);
        inputMax.value = String(hi);
        updateVisual();
        onRangeChange(state, filter, lo, hi);
      };
      txtMin.addEventListener("change", commitFromText);
      txtMax.addEventListener("change", commitFromText);
      txtMin.addEventListener("keydown", e => { if (e.key === "Enter") commitFromText(); });
      txtMax.addEventListener("keydown", e => { if (e.key === "Enter") commitFromText(); });

      syncText();
    }

    // Inputs boven slider voor prijs (Bol-stijl); anders eronder
    if (inputsTop && inputsRow) frag.appendChild(inputsRow);
    frag.appendChild(slider);
    if (!inputsTop && inputsRow) frag.appendChild(inputsRow);

    return frag;
  }

  function formatRangeValue(n, filter) {
    const rounded = filter.step < 1
      ? Math.round(n * 10) / 10
      : Math.round(n);
    return filter.unit ? `${rounded} ${filter.unit}` : String(rounded);
  }

  function renderActiveChips(state) {
    const wrap = document.querySelector(".cf-active-wrap");
    const chipContainer = document.querySelector(".cf-active-chips");
    if (!wrap || !chipContainer) return;

    const chips = [];
    for (const [shortKey, val] of state.activeByKey) {
      const f = state.filters.find(x => x.shortKey === shortKey);
      if (!f) continue;
      if (f._uiType === "range") {
        chips.push({
          shortKey, label: `${f.display}: ${formatRangeValue(val.min, f)} – ${formatRangeValue(val.max, f)}`,
          onRemove: () => { state.activeByKey.delete(shortKey); commit(state); },
        });
      } else if (f._uiType === "bucket" && val instanceof Set) {
        for (const idxStr of val) {
          const bucket = f.buckets[parseInt(idxStr, 10)];
          if (!bucket) continue;
          chips.push({
            shortKey, value: idxStr, label: `${f.display}: ${bucket.label}`,
            onRemove: () => {
              val.delete(idxStr);
              if (val.size === 0) state.activeByKey.delete(shortKey);
              commit(state);
            },
          });
        }
      } else if (val instanceof Set) {
        for (const v of val) {
          const text = labelForOption(f, v);
          chips.push({
            shortKey, value: v, label: `${f.display}: ${text}`,
            onRemove: () => {
              val.delete(v);
              if (val.size === 0) state.activeByKey.delete(shortKey);
              commit(state);
            },
          });
        }
      }
    }

    chipContainer.innerHTML = "";
    const frag = document.createDocumentFragment();
    for (const chip of chips) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "cf-chip";
      btn.innerHTML = `<span>${escapeHtml(chip.label)}</span><span class="cf-chip-x" aria-hidden="true">×</span>`;
      btn.addEventListener("click", chip.onRemove);
      frag.appendChild(btn);
    }
    chipContainer.appendChild(frag);
    wrap.hidden = chips.length === 0;

    // Render dezelfde chips ALSO boven het product-grid (Bol-stijl)
    renderActiveChipsBar(state, chips);

    // Update mobile-toggle badge count.
    const badge = document.querySelector(".cf-active-badge");
    if (badge) {
      badge.textContent = String(chips.length);
      badge.hidden = chips.length === 0;
    }
  }

  function renderActiveChipsBar(state, chips) {
    // Bouw een chip-bar BOVEN het product-grid (zoals Bol).
    let bar = document.querySelector(".cf-active-chips-bar");
    const gridResults = document.querySelector(".main-products-grid__results");
    if (!gridResults) return;
    if (!bar) {
      bar = document.createElement("div");
      bar.className = "cf-active-chips-bar";
      gridResults.insertBefore(bar, gridResults.firstChild);
    }
    if (chips.length === 0) {
      bar.hidden = true;
      bar.innerHTML = "";
      return;
    }
    bar.hidden = false;
    bar.innerHTML = "";
    const frag = document.createDocumentFragment();
    for (const chip of chips) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "cf-chip";
      btn.innerHTML = `<span>${escapeHtml(chip.label)}</span><span class="cf-chip-x" aria-hidden="true">×</span>`;
      btn.addEventListener("click", chip.onRemove);
      frag.appendChild(btn);
    }
    // Wis-alle knop achter chips
    const clear = document.createElement("button");
    clear.type = "button";
    clear.className = "cf-clear-all cf-clear-all-bar";
    clear.textContent = "Wis alle filters";
    frag.appendChild(clear);
    bar.appendChild(frag);
  }

  function syncUiFromState(state) {
    for (const f of state.filters) {
      const block = document.querySelector(`.cf-filter[data-key="${CSS.escape(f.shortKey)}"]`);
      if (!block) continue;
      const active = state.activeByKey.get(f.shortKey);
      if (f._uiType === "range") {
        const [inMin, inMax] = block.querySelectorAll(".cf-range-input");
        if (!inMin || !inMax) continue;
        inMin.value = String(active?.min ?? f._min);
        inMax.value = String(active?.max ?? f._max);
        inMin.dispatchEvent(new Event("input"));  // trigger visual update
      } else {
        const cbs = block.querySelectorAll("input[type='checkbox']");
        for (const cb of cbs) cb.checked = active ? active.has(cb.value) : false;
      }
    }
  }

  // =========================================================================
  // 9. EVENT HANDLERS
  // =========================================================================
  function onCheckboxChange(state, filter, value, checked) {
    let set = state.activeByKey.get(filter.shortKey);
    if (!set) {
      set = new Set();
      state.activeByKey.set(filter.shortKey, set);
    }
    if (checked) set.add(value); else set.delete(value);
    if (set.size === 0) state.activeByKey.delete(filter.shortKey);
    commit(state);
  }

  function onRangeChange(state, filter, lo, hi) {
    if (lo === filter._min && hi === filter._max) {
      state.activeByKey.delete(filter.shortKey);
    } else {
      state.activeByKey.set(filter.shortKey, { min: lo, max: hi });
    }
    commit(state);
  }

  function commit(state) {
    // Filter-change reset paginatie naar pagina 1
    state.currentPage = 1;
    writeUrlFromState(state, true);
    applyFilters(state);
    renderActiveChips(state);
    syncUiFromState(state);
    // updateFilterCounts is zwaar (alle filters × cards × labels). Defer naar
    // de volgende frame zodat de browser eerst een paint kan doen voor de
    // click-feedback voltooit. Voorkomt "hangen" gevoel bij bulk-clears.
    if (state._countsRaf) cancelAnimationFrame(state._countsRaf);
    state._countsRaf = requestAnimationFrame(() => {
      updateFilterCounts(state);
      state._countsRaf = null;
    });
  }

  // Surgical update: alleen count-tekst + zichtbaarheid van opties bijwerken.
  // GEEN DOM-rebuild zodat klikken niet onderbroken raken.
  function updateFilterCounts(state) {
    for (const filter of state.filters) {
      const block = document.querySelector(`.cf-filter[data-key="${CSS.escape(filter.shortKey)}"]`);
      if (!block) continue;
      if (filter._uiType === "range") continue;

      const pool = state.cards.filter(c => cardMatchesExcluding(c, state, filter.shortKey));
      const labels = block.querySelectorAll(".cf-checkbox");

      labels.forEach(label => {
        const cb = label.querySelector("input[type='checkbox']");
        const countEl = label.querySelector(".cf-checkbox-count");
        if (!cb || !countEl) return;

        let count = 0;
        if (filter._uiType === "compound") {
          const opt = filter.options.find(o => o.id === cb.value);
          if (opt) count = pool.filter(card => compoundOptionMatches(card, opt)).length;
          countEl.textContent = `(${count})`;
        } else if (filter._uiType === "bucket") {
          const idx = parseInt(cb.value, 10);
          const bucket = filter.buckets[idx];
          if (bucket) {
            for (const card of pool) {
              const n = Number(card.values[filter.shortKey]);
              if (Number.isNaN(n)) continue;
              const okMin = bucket.min === undefined || n >= bucket.min;
              const okMax = bucket.max === undefined || n < bucket.max;
              if (okMin && okMax) count++;
            }
          }
          countEl.textContent = `(${count})`;
        } else {
          // checkbox
          for (const card of pool) {
            const v = card.values[filter.shortKey];
            if (v === undefined || v === null || v === "") continue;
            const vals = Array.isArray(v) ? v : [v];
            if (vals.some(x => String(x) === cb.value)) count++;
          }
          countEl.textContent = String(count);
        }

        // Verberg optie als count=0 en niet geselecteerd
        if (count === 0 && !cb.checked) label.classList.add("cf-option-zero");
        else label.classList.remove("cf-option-zero");
      });

      // Re-evalueer collapse: hertoewijzing van cf-option-hidden op basis van
      // de actueel zichtbare (non-zero) opties. Verberg de "Meer"-knop als
      // alles binnen de zichtbare threshold past.
      const allLabels = Array.from(labels);
      const effective = allLabels.filter(l => !l.classList.contains("cf-option-zero"));
      const toggleBtn = block.querySelector(".cf-options-toggle");
      const wrap = block.querySelector(".cf-checkbox-list");

      if (effective.length > MAX_VISIBLE_OPTIONS) {
        effective.forEach((label, i) => {
          if (i >= MAX_VISIBLE_OPTIONS) label.classList.add("cf-option-hidden");
          else label.classList.remove("cf-option-hidden");
        });
        if (toggleBtn) toggleBtn.hidden = false;
      } else {
        effective.forEach(l => l.classList.remove("cf-option-hidden"));
        if (wrap) wrap.classList.remove("cf-options-expanded");
        if (toggleBtn) {
          toggleBtn.hidden = true;
          toggleBtn.setAttribute("aria-expanded", "false");
          const txt = toggleBtn.querySelector(".cf-options-toggle-text");
          if (txt) txt.textContent = "Meer";
        }
      }
    }
  }

  function clearAllFilters(state) {
    state.activeByKey = new Map();
    // Instant visuele feedback: verberg chips/bars meteen zodat de gebruiker
    // direct ziet dat de klik geregistreerd is.
    const bar = document.querySelector(".cf-active-chips-bar");
    if (bar) { bar.hidden = true; bar.innerHTML = ""; }
    const activeWrap = document.querySelector(".cf-active-wrap");
    if (activeWrap) activeWrap.hidden = true;
    // Defer commit naar de volgende macrotask zodat de click-event volledig
    // released wordt (anders blijft de knop "hangen" tot updateFilterCounts klaar is).
    setTimeout(() => commit(state), 0);
  }

  function attachClearAll(state) {
    // Event-delegation op document: werkt ook als .cf-clear-all later re-rendered wordt
    // of als initial-click te vroeg gebeurt voordat de wrap zichtbaar is.
    document.addEventListener("click", (e) => {
      const btn = e.target.closest(".cf-clear-all, .cf-empty-clear");
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      // Defocus zodat :focus/:hover visuele states niet "blijven hangen"
      // (op desktop blijft :hover anders op de cursor-positie staan).
      btn.blur();
      log("clearAllFilters triggered via", btn.className);
      clearAllFilters(state);
    });
  }

  // =========================================================================
  // 10. MOBILE DRAWER
  // =========================================================================
  function attachDrawerHandlers() {
    const toggle = document.querySelector(".cf-mobile-toggle");
    const drawer = document.getElementById("cf-drawer");
    const overlay = document.querySelector(".cf-overlay");
    const close = drawer?.querySelector(".cf-close");
    const apply = drawer?.querySelector(".cf-apply");
    if (!toggle || !drawer || !overlay) return;

    // Show the mobile-toggle only on small viewports. CSS handles the actual visibility,
    // but we remove the `hidden` attribute so the CSS can take over.
    toggle.hidden = false;

    const open = () => {
      drawer.classList.add("cf-open");
      overlay.hidden = false;
      toggle.setAttribute("aria-expanded", "true");
      document.body.style.overflow = "hidden";
    };
    const shut = () => {
      drawer.classList.remove("cf-open");
      overlay.hidden = true;
      toggle.setAttribute("aria-expanded", "false");
      document.body.style.overflow = "";
    };
    toggle.addEventListener("click", open);
    overlay.addEventListener("click", shut);
    close?.addEventListener("click", shut);
    apply?.addEventListener("click", shut);
    document.addEventListener("keydown", e => {
      if (e.key === "Escape" && drawer.classList.contains("cf-open")) shut();
    });
  }

  // Move the mobile-toggle button into <products-toolbar> as its first child.
  // Idempotent: no-op when already first child, or when either element is absent.
  function moveMobileToggleToToolbar() {
    const toggle = document.querySelector(".cf-mobile-toggle");
    const toolbar = document.querySelector("products-toolbar");
    if (!toggle || !toolbar) return;
    if (toolbar.firstElementChild === toggle) return;
    toolbar.prepend(toggle);
  }

  // =========================================================================
  // 11. ENTRY
  // =========================================================================
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
